// Регистрация Service Worker через vite-plugin-pwa.
// registerType: 'autoUpdate' — новая версия применяется автоматически: при
// обнаружении обновления SW сразу активируется (skipWaiting) и страница
// перезагружается на свежую сборку. Баннера «Доступно обновление» больше нет.
//
// Внимание: virtual:pwa-register резолвится только при сборке через vite-plugin-pwa.
// В Vitest/jsdom этот модуль не импортится — функция safe-no-op.

/** Как часто открытая вкладка спрашивает сервер про новую версию. */
const UPDATE_CHECK_MS = 60_000;

export interface ServiceWorkerRegistrationCallbacks {
  onNeedRefresh?: () => void;
  onOfflineReady?: () => void;
  onRegisterError?: (error: unknown) => void;
}

export function registerServiceWorker(callbacks: ServiceWorkerRegistrationCallbacks = {}): void {
  // Не пытаемся регистрировать SW в dev/test — Vite сам обработает в production билде.
  if (typeof window === 'undefined') return;
  if (import.meta.env.MODE !== 'production') return;
  if (!('serviceWorker' in navigator)) return;

  // Динамический импорт virtual-модуля, чтобы tsc/vitest не падали без vite-plugin-pwa.
  // eslint-disable-next-line import/no-unresolved -- virtual:pwa-register предоставляет vite-plugin-pwa в рантайме.
  void import(/* @vite-ignore */ 'virtual:pwa-register')
    .then(({ registerSW }) => {
      const updateSW = registerSW({
        // immediate: регистрируем сразу, без ожидания события 'load'.
        immediate: true,
        // Открытая сутками вкладка сама спрашивает сервер про новую версию:
        // иначе кассир, не закрывающий CRM, живёт на сборке недельной давности
        // (правка 2026-07-31).
        onRegisteredSW(_swUrl: string, registration: ServiceWorkerRegistration | undefined) {
          if (!registration) return;
          setInterval(() => {
            void registration.update();
          }, UPDATE_CHECK_MS);

          // На телефоне таймера мало: свёрнутое приложение усыпляется, и
          // setInterval не тикает вовсе. Человек возвращается в CRM и работает
          // на сборке, которая была актуальной в момент последнего открытия —
          // так у второго администратора 28.08 осталась вчерашняя версия,
          // хотя он переустанавливал приложение. Спрашиваем сервер о новой
          // версии КАЖДЫЙ раз, когда приложение снова становится видимым.
          document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') void registration.update();
          });
        },
        onNeedRefresh() {
          // autoUpdate: применяем новую версию немедленно (skipWaiting + reload),
          // без подтверждения пользователем.
          void updateSW(true);
          callbacks.onNeedRefresh?.();
        },
        onOfflineReady() {
          // eslint-disable-next-line no-console
          console.info('[sw] offline ready');
          callbacks.onOfflineReady?.();
        },
        onRegisterError(error: unknown) {
          // eslint-disable-next-line no-console
          console.error('[sw] register error', error);
          callbacks.onRegisterError?.(error);
        },
      });
    })
    .catch((error) => {
      // virtual:pwa-register не доступен — это нормально в test/dev.
      // eslint-disable-next-line no-console
      console.debug('[sw] virtual:pwa-register not available', error);
    });
}
